import { ChangeDetectorRef, Inject, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { DashboardService } from '@core/services/employee/dashboard.service';
import { InjectorKeys } from '@core/utils/injector.keys';
import { GalaxyDrawerBlockingService, MenuItem, slideInFromRight } from '@ebcont/ebcont.galaxy.js';
import { catchError, forkJoin, Observable, of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

// this is in order in the UI
enum OverviewKeys {
  COMPANIES = 'companies',
  CITIES = 'cities',
  GREMIEN = 'gremien',
  AREAS = 'areas',
  PROCURATION = 'authorities',
  COUNTRIES = 'countries'
}

@Component({
  selector: 'app-overview-list',
  templateUrl: './overview-list.component.html',
  styleUrl: './overview-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  animations: [slideInFromRight]
})
export class OverviewListComponent implements OnInit {
  overviewData$: Observable<any[]> = of([]);
  loading: boolean = true;
  mapElements: any[] = [];
  selectedPath: any[] = [];
  employeeEmail?: string = undefined;
  selectedKey?: OverviewKeys = undefined;
  sortAscending: boolean = true;
  private drawerCloseSubscription: Subscription | undefined;
  sortingItems: MenuItem[] = [
    { label: 'sorting.asc', id: 'asc', isRouterLink: false },
    { label: 'sorting.desc', id: 'desc', isRouterLink: false }
  ];

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(InjectorKeys.DASHBOARD) private dashboardService: DashboardService,
    private drawerBlockingService: GalaxyDrawerBlockingService
  ) {}

  ngOnInit(): void {
    this.overviewData$ = forkJoin({
      authorities: this.dashboardService.getAuthoritiesToSignChart(),
      cities: this.dashboardService.getCities(),
      companies: this.dashboardService.getCompanies(),
      gremien: this.dashboardService.getGremium(),
      areas: this.dashboardService.getAreas(),
      countries: this.dashboardService.getCountries()
    }).pipe(
      map((results) => {
        const combinedData = Object.values(OverviewKeys)
          .filter(key => results[key])
          .map(key => ({
            ...results[key],
            overviewKey: key,
          }));
        this.loading = false;
        this.changeDetectorRef.detectChanges();
        return combinedData;
      }),
      catchError((error) => {
        this.loading = false;
        this.changeDetectorRef.detectChanges();
        return of([]);
      })
    );

    this.drawerCloseSubscription = this.drawerBlockingService.closeNotification$.subscribe(() => {
      this.closeEmployeeDetails();
    });
  }

  ngOnDestroy(): void {
    this.drawerCloseSubscription?.unsubscribe();
  }

  onItemClick(item: any, level: number): void {
    this.employeeEmail = item.email;
    if (level === 0) {
      this.selectedKey = item.overviewKey ?? undefined;
      this.mapElements = this.selectedKey === OverviewKeys.COUNTRIES || this.selectedKey === OverviewKeys.CITIES ? item.children : [];
    }
    this.selectedPath = this.selectedPath.slice(0, level);
    if (item.children?.length > 0) {
      this.selectedPath.push(item);
    }
  }

  goBack(): void {
    if (this.selectedPath.length === 0) return;
    this.selectedPath.pop();
    if (this.level === -1) this.mapElements = [];
  }

  get level() {
    return this.selectedPath.length - 1;
  }

  closeMap() {
    this.mapElements = [];
  }

  closeEmployeeDetails(): void {
    this.employeeEmail = undefined;
  }

  isEmployeeLevel(children: any): boolean {
    return children?.length > 0 && !!children[0].email;
  }

  isItemSelected(item: any, level: number): boolean {
    const isPathSelected = this.selectedPath[level + 1] === item;
    const isEmployeeSelected = !!this.employeeEmail && this.employeeEmail === item.email;
    return isPathSelected || isEmployeeSelected;
  }

  isLevelVisible(level: number): boolean {
    return this.selectedPath.length - level === 1;
  }

  getSubheadlineIdForKeyAnDepth(depth: number): string {
    if (!this.selectedKey) return 'DEFAULT';
    return `overview.subheadlines.${this.selectedKey}.${depth}`
  }

  toggleSortOrder() {
    this.sortAscending = !this.sortAscending;
  }

  sortItems(items: any[], depth: number): any[] {
    // don't sort Overview and Management & representation level
    if (depth < 0 || (this.selectedKey === OverviewKeys.PROCURATION && depth === 1)) return [...items];

    return [...items].sort((a, b) => {
      const diff =
        (b.children?.length ?? 0) - (a.children?.length ?? 0) ||
        (b.count ?? 0) - (a.count ?? 0);
      if (diff !== 0) return diff;

      if (a.familyName && b.familyName) {
        return this.sortAscending
          ? a.familyName.localeCompare(b.familyName)
          : b.familyName.localeCompare(a.familyName);
      }

      return a.name.localeCompare(b.name);
    });
  }
}
